import { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { Loader } from "@googlemaps/js-api-loader";

interface GooglePlacesAddressProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  id?: string;
  name?: string;
  required?: boolean;
  onPlaceSelect?: (place: google.maps.places.PlaceResult) => void;
  onCoordinatesChange?: (coords: { lat: number; lng: number } | null) => void;
}

export const GooglePlacesAddress = ({
  value,
  onChange,
  placeholder = "Enter full address",
  className,
  id,
  name,
  required,
  onPlaceSelect,
  onCoordinatesChange
}: GooglePlacesAddressProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [googleMapsKey, setGoogleMapsKey] = useState<string | null>(null);


  // Fetch Google Maps API key
  useEffect(() => {
    const fetchGoogleMapsKey = async () => {
      try {
        const response = await fetch('https://asrkgtsboqenyuetcjuj.supabase.co/functions/v1/get-google-maps-key', {
          headers: {
            'Authorization': `Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImFzcmtndHNib3Flbnl1ZXRjanVqIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTY4NjY0NjQsImV4cCI6MjA3MjQ0MjQ2NH0.IbmQpxyInbEBlRU_3kd-vdiHmlw7tgbhEk3j7LRpCck`,
            'apikey': 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImFzcmtndHNib3Flbnl1ZXRjanVqIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTY4NjY0NjQsImV4cCI6MjA3MjQ0MjQ2NH0.IbmQpxyInbEBlRU_3kd-vdiHmlw7tgbhEk3j7LRpCck'
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          setGoogleMapsKey(data.key);
        } else {
          console.error('Failed to fetch Google Maps key');
        }
      } catch (error) {
        console.error('Error fetching Google Maps key:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchGoogleMapsKey();
  }, []);

  // Initialize Google Places Autocomplete
  useEffect(() => {
    if (!inputRef.current || !googleMapsKey || isLoading) return;
    
    // Clean up existing autocomplete before creating new one
    if (autocompleteRef.current && window.google) {
      window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
      autocompleteRef.current = null;
    }

    const initAutocomplete = async () => {
      try {
        console.log('🚀 GooglePlaces: Starting autocomplete initialization');
        const loader = new Loader({
          apiKey: googleMapsKey,
          version: "weekly",
          libraries: ["places"],
          language: "en"
        });

        console.log('🚀 GooglePlaces: Loading Google Maps API...');
        await loader.load();
        console.log('🚀 GooglePlaces: Google Maps API loaded successfully');

        // Double check input still exists after async load
        if (!inputRef.current) {
          console.warn('🚀 GooglePlaces: Input ref lost during async load');
          return;
        }

        console.log('🚀 GooglePlaces: Creating autocomplete instance...');
        const autocomplete = new window.google.maps.places.Autocomplete(inputRef.current, {
          componentRestrictions: { country: "gb" },
          fields: ["formatted_address", "address_components", "geometry", "name"],
          types: ["address"]
        });

        autocompleteRef.current = autocomplete;
        console.log('🚀 GooglePlaces: Autocomplete instance created successfully');

        const handlePlaceChanged = () => {
          try {
            console.log('🔍 GooglePlaces: place_changed event triggered');
            const place = autocomplete.getPlace();
            console.log('🔍 GooglePlaces: place data received:', place);
            
            if (!place) {
              console.warn('🔍 GooglePlaces: No place object received');
              return;
            }
            
            if (!place.geometry) {
              console.warn('🔍 GooglePlaces: No geometry in place object');
              return;
            }
            
            const selectedText = place.formatted_address 
              ?? place.name 
              ?? (inputRef.current?.value || '');
              
            console.log('🔍 GooglePlaces: selected text:', selectedText);
              
            if (selectedText) {
              console.log('🔍 GooglePlaces: calling onChange with:', selectedText);
              
              // Use setTimeout to ensure the change happens after the dropdown interaction completes
              setTimeout(() => {
                onChange(selectedText);
                onPlaceSelect?.(place);
              }, 0);
              
              console.debug('GooglePlacesAddress selected:', { selectedText, place });
            }
            
            // Extract coordinates
            const coords = place.geometry?.location ? {
              lat: typeof place.geometry.location.lat === 'function'
                ? place.geometry.location.lat()
                : (place.geometry.location.lat as unknown as number),
              lng: typeof place.geometry.location.lng === 'function'
                ? place.geometry.location.lng()
                : (place.geometry.location.lng as unknown as number)
            } : null;
            
            console.log('🔍 GooglePlaces: extracted coords:', coords);
            onCoordinatesChange?.(coords);
          } catch (error) {
            console.error('🔍 GooglePlaces: Error in handlePlaceChanged:', error);
          }
        };

        autocomplete.addListener("place_changed", handlePlaceChanged);

      } catch (error) {
        console.error("Error initializing Google Places Autocomplete:", error);
        // Silently continue - component will work as regular input
      }
    };

    // Add small delay to ensure DOM is ready
    const timeoutId = setTimeout(initAutocomplete, 100);

    return () => {
      clearTimeout(timeoutId);
      if (autocompleteRef.current && window.google) {
        window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
        autocompleteRef.current = null;
      }
    };
  }, [googleMapsKey, isLoading, onChange, onPlaceSelect, onCoordinatesChange]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
    }
  };

  const handleBlur = () => {
    const current = inputRef.current?.value ?? '';
    if (current !== value) {
      onChange(current);
    }
  };

  // Prevent dialog closure when interacting with Google Places dropdown
  const handleContainerClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  if (isLoading) {
    return (
      <Input
        value=""
        placeholder="Loading address services..."
        disabled
        className={cn(className)}
      />
    );
  }

  if (!googleMapsKey) {
    return (
      <Input
        value={value}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        placeholder={placeholder}
        className={cn(className)}
        id={id}
        name={name}
        required={required}
      />
    );
  }

  return (
    <div 
      onClick={handleContainerClick}
      onMouseDown={handleMouseDown}
      className="pointer-events-auto"
    >
      <Input
        ref={inputRef}
        value={value}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        placeholder={placeholder}
        className={cn(className)}
        id={id}
        name={name}
        required={required}
        autoComplete="off"
      />
    </div>
  );
};